import { useCallback, useEffect, useMemo } from 'react';
import { Appearance, Platform, useColorScheme } from 'react-native';
import { useAppContext } from '../contexts/AppContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAppStore } from '../store/appStore';
import { useCartStore } from '../store/cartStore';

/**
 * Link params a storefront page (catalog / product / cart / checkout /
 * success) carries forward so every later step still knows which business,
 * contact, conversation and catalog the customer came from.
 */
export const STOREFRONT_PARAM_KEYS = [
  'itemId',
  'businessId',
  'contactId',
  'conversationId',
  'phoneNumber',
  'catalogId',
  'productId',
  'language',
  'quickReplyItemId',
  // The conversation's signed cart key: the page's cart is that conversation's shared cart.
  'ct',
] as const;

export type StorefrontParams = Partial<Record<(typeof STOREFRONT_PARAM_KEYS)[number], string>>;

/**
 * Shared setup for the customer-facing storefront screens opened from a
 * product/catalog message link.
 *
 * - Theme: a customer (not signed in) gets their device/browser light or
 *   dark setting, live — not the app's stored default. A signed-in business
 *   user previewing the store keeps their own app theme.
 * - Language: the link's `language` param (set by the quick-reply item's
 *   order customization) picks Arabic/English for a customer.
 * - Cart: scoped to the link's business, so items from another store never
 *   leak into this checkout.
 */
export function useStorefront() {
  const { routeParams, navigateTo } = useAppContext();
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const currentBusinessId = useAppStore((s) => s.currentBusiness?._id);
  const uiLocale = useAppStore((s) => s.uiLocale);
  const setUiLocale = useAppStore((s) => s.setUiLocale);
  const { themeMode, setThemeMode } = useTheme();
  const systemScheme = useColorScheme();
  const ensureCartBusiness = useCartStore((s) => s.ensureBusiness);

  const params: StorefrontParams = useMemo(() => {
    const out: StorefrontParams = {};
    const fromUrl =
      Platform.OS === 'web' && typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    for (const key of STOREFRONT_PARAM_KEYS) {
      const v = fromUrl?.get(key) ?? (routeParams as Record<string, any> | undefined)?.[key];
      if (v !== undefined && v !== null && v !== '' && v !== 'null' && v !== 'undefined') out[key] = String(v);
    }
    if (!out.businessId && currentBusinessId) out.businessId = currentBusinessId;
    if (!out.quickReplyItemId && out.itemId) out.quickReplyItemId = out.itemId;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams, currentBusinessId]);

  // Device theme for customers — follows changes while the page is open.
  useEffect(() => {
    if (isAuthenticated) return;
    const apply = (scheme: string | null | undefined) => {
      const mode = scheme === 'dark' ? 'dark' : 'light';
      if (mode !== themeMode) setThemeMode(mode);
    };
    apply(systemScheme ?? Appearance.getColorScheme());
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = (e: MediaQueryListEvent) => apply(e.matches ? 'dark' : 'light');
      mq.addEventListener?.('change', onChange);
      return () => mq.removeEventListener?.('change', onChange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, systemScheme]);

  // Link language for customers.
  useEffect(() => {
    if (isAuthenticated || !params.language) return;
    const lang = params.language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
    if (!String(uiLocale || '').startsWith(lang)) setUiLocale(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, params.language]);

  useEffect(() => {
    if (params.businessId) ensureCartBusiness(params.businessId);
    // From a conversation's link: the same cart the AI and the team see.
    if (params.businessId && params.conversationId && params.ct) {
      import('../store/sharedCartSync').then(({ startSharedCart }) =>
        startSharedCart({ businessId: params.businessId!, conversationId: params.conversationId!, ct: params.ct! }));
    }
  }, [params.businessId, params.conversationId, params.ct, ensureCartBusiness]);

  /** Navigate to another storefront page, carrying the link context along. */
  const go = useCallback(
    (screen: 'catalog-view' | 'product-view' | 'cart' | 'checkout' | 'order-success' | 'track-order', extra?: Record<string, string | undefined>) => {
      const next: Record<string, string> = {};
      Object.entries({ ...params, ...(extra || {}) }).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') next[k] = String(v);
      });
      navigateTo(screen as any, undefined, next);
    },
    [params, navigateTo]
  );

  return { params, go, isAuthenticated };
}
